import React, { useState, useEffect } from 'react';
import { 
  Shield, Clock, Users, BarChart3, Bell, FileText, CheckCircle, Star, 
  AlertTriangle, Calendar, Zap, ArrowRight, ChevronDown, ChevronUp,
  Brain, Smartphone, Lock, Award, TrendingUp, Target, MessageSquare, Menu, X
} from 'lucide-react';

const LandingPage = ({ onNavigate }) => {
  const [openFaq, setOpenFaq] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Detectar scroll para mudar estilo do header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const testimonials = [
    {
      name: "Dr. Carlos Silva",
      role: "Advogado Trabalhista",
      city: "São Paulo/SP",
      text: "Antes eu perdia horas conferindo prazos manualmente. Com o JurisPrazo, a IA já detecta tudo automaticamente. Nunca mais perdi um prazo!",
      avatar: "👨‍⚖️",
      rating: 5
    },
    {
      name: "Dra. Ana Beatriz",
      role: "Advogada Cível",
      city: "Rio de Janeiro/RJ",
      text: "O sistema salvou meu escritório! Recebi um alerta de prazo fatal que eu tinha esquecido. Vale cada centavo.",
      avatar: "👩‍⚖️",
      rating: 5
    },
    {
      name: "Dr. Roberto Mendes",
      role: "Escritório de Advocacia",
      city: "Belo Horizonte/MG",
      text: "Gerencio 200+ processos e agora tenho controle total. O calendário visual é incrível para reuniões com clientes.",
      avatar: "🏛️",
      rating: 5
    }
  ];

  const faqs = [
    {
      q: "O sistema funciona com quais tribunais?",
      a: "O JurisPrazo integra com o sistema Comunica PJe, que abrange praticamente todos os tribunais do país. Você cadastra sua OAB e o sistema busca automaticamente todas as intimações e publicações."
    },
    {
      q: "Como a IA detecta os prazos?",
      a: "Nossa inteligência artificial (GPT-4) analisa o texto de cada comunicação judicial, identifica prazos explícitos e implícitos, calcula automaticamente as datas de vencimento considerando dias úteis/corridos conforme o rito processual, e sugere ações a serem tomadas."
    },
    {
      q: "Meus dados estão seguros?",
      a: "Sim! Utilizamos Firebase (Google Cloud) com criptografia de ponta a ponta, autenticação segura e conformidade total com a LGPD. Seus dados nunca são compartilhados."
    },
    {
      q: "Posso testar antes de assinar?",
      a: "Claro! Oferecemos um período de teste gratuito para você experimentar todas as funcionalidades sem compromisso."
    },
    {
      q: "Funciona no celular?",
      a: "Sim! O JurisPrazo é totalmente responsivo e funciona perfeitamente em smartphones e tablets, permitindo que você acompanhe seus prazos de qualquer lugar."
    }
  ];

  const steps = [
    {
      step: "1",
      title: "Cadastre sua OAB",
      desc: "Insira seu número de registro e seccional da OAB",
      icon: Users
    },
    {
      step: "2",
      title: "Busque Comunicações",
      desc: "O sistema conecta ao PJe e baixa todas as intimações",
      icon: FileText
    },
    {
      step: "3",
      title: "IA Detecta Prazos",
      desc: "Inteligência artificial analisa e calcula datas",
      icon: Brain
    },
    {
      step: "4",
      title: "Receba Alertas",
      desc: "Nunca mais perca um prazo importante",
      icon: Bell
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header Fixo */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isScrolled ? 'bg-gradient-to-br from-blue-500 to-indigo-600' : 'bg-white/20 backdrop-blur-sm'
              }`}>
                <Calendar className={`w-6 h-6 ${isScrolled ? 'text-white' : 'text-white'}`} />
              </div>
              <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                JurisPrazo
              </span>
            </div>

            {/* Nav Desktop */}
            <nav className="hidden md:flex items-center gap-6">
              <a 
                href="#como-funciona" 
                className={`text-sm font-medium transition-colors ${
                  isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                }`}
              >
                Como Funciona
              </a>
              <a 
                href="#beneficios" 
                className={`text-sm font-medium transition-colors ${
                  isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                }`}
              >
                Benefícios
              </a>
            </nav>

            {/* Botões Auth Desktop */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => onNavigate && onNavigate('auth:login')}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                  isScrolled 
                    ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Entrar
              </button>
              <button
                onClick={() => onNavigate && onNavigate('auth:cadastro')}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                  isScrolled 
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md' 
                    : 'bg-white text-blue-700 hover:bg-gray-100'
                }`}
              >
                Criar Conta Grátis
              </button>
            </div>

            {/* Menu Mobile */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg ${isScrolled ? 'text-gray-700' : 'text-white'}`}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Menu Mobile Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200 bg-white rounded-b-xl shadow-lg">
              <div className="flex flex-col gap-2 px-2">
                <a href="#como-funciona" className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                  Como Funciona
                </a>
                <a href="#beneficios" className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                  Benefícios
                </a>
                <hr className="my-2" />
                <button
                  onClick={() => onNavigate && onNavigate('auth:login')}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-left"
                >
                  Entrar
                </button>
                <button
                  onClick={() => onNavigate && onNavigate('auth:cadastro')}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg"
                >
                  Criar Conta Grátis
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section - Impactante */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white pt-20">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
              <Brain className="w-5 h-5 text-yellow-400" />
              <span className="text-sm font-medium">Powered by Inteligência Artificial</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              Nunca Mais <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">Perca um Prazo</span>
              <br />
              <span className="text-3xl md:text-5xl lg:text-6xl">Processual</span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-10">
              A <strong>única plataforma</strong> que busca suas intimações do PJe e usa 
              <strong className="text-yellow-400"> IA para detectar prazos automaticamente</strong>.
              Feito por advogados, para advogados.
            </p>

            {/* Pain Point */}
            <div className="bg-red-500/20 border border-red-400/30 rounded-2xl p-4 md:p-6 max-w-2xl mx-auto mb-10">
              <div className="flex items-center justify-center gap-3 text-red-300 mb-2">
                <AlertTriangle className="w-6 h-6" />
                <span className="font-bold text-lg">Você sabia?</span>
              </div>
              <p className="text-white/90">
                <strong>23% dos advogados</strong> já perderam prazos por falta de controle adequado.
                Um prazo perdido pode significar <strong className="text-red-400">processo disciplinar na OAB</strong> e 
                <strong className="text-red-400"> indenização ao cliente</strong>.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => onNavigate && onNavigate('auth:cadastro')}
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 rounded-xl font-bold text-slate-900 text-lg transition-all duration-300 shadow-2xl hover:shadow-yellow-500/25 transform hover:-translate-y-1"
              >
                Começar Gratuitamente
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={() => document.getElementById('como-funciona').scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 hover:border-white/50 rounded-xl font-semibold text-white text-lg transition-all duration-300"
              >
                Ver Como Funciona
              </button>
            </div>

            {/* Social Proof Mini */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-blue-200">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Sem cartão de crédito</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Setup em 2 minutos</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Suporte brasileiro</span>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Chega de <span className="text-red-500">Perder Tempo</span> com Controle Manual
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advogados gastam em média <strong>2 horas por dia</strong> verificando prazos manualmente.
              Com o JurisPrazo, esse tempo é <strong>ZERO</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Pain 1 */}
            <div className="relative p-8 bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-3xl">
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                ❌
              </div>
              <h3 className="text-xl font-bold text-red-800 mb-4">Planilhas Desatualizadas</h3>
              <p className="text-red-700">
                Controlar prazos em Excel é arriscado. Uma célula errada e você perde o prazo fatal.
              </p>
            </div>

            {/* Pain 2 */}
            <div className="relative p-8 bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-3xl">
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                ❌
              </div>
              <h3 className="text-xl font-bold text-red-800 mb-4">Cálculo Manual de Prazos</h3>
              <p className="text-red-700">
                Dias úteis, dias corridos, feriados... Calcular prazo manualmente é fonte de erro.
              </p>
            </div>

            {/* Pain 3 */}
            <div className="relative p-8 bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-3xl">
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                ❌
              </div>
              <h3 className="text-xl font-bold text-red-800 mb-4">Muitos Sistemas para Verificar</h3>
              <p className="text-red-700">
                Acessar cada tribunal, cada sistema... É impossível não esquecer algo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 rounded-full px-4 py-2 mb-6">
              <CheckCircle className="w-5 h-5" />
              <span className="font-semibold">A Solução</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              JurisPrazo Resolve <span className="text-blue-600">Tudo Isso</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Busca Automática</h3>
              <p className="text-gray-600">
                Conecta ao PJe e baixa <strong>todas as intimações</strong> automaticamente. Você não precisa acessar nenhum tribunal.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">IA que Entende Jurídico</h3>
              <p className="text-gray-600">
                Nossa IA lê cada intimação, <strong>detecta prazos</strong>, calcula datas de vencimento e sugere ações.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Calendário Visual</h3>
              <p className="text-gray-600">
                Visualize <strong>todos os prazos</strong> em um calendário moderno. Cores indicam urgência: vermelho, amarelo, verde.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="group bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Bell className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Alertas Inteligentes</h3>
              <p className="text-gray-600">
                Receba <strong>notificações</strong> quando um prazo se aproxima. Nunca seja pego de surpresa.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="group bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Smartphone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Acesse de Qualquer Lugar</h3>
              <p className="text-gray-600">
                Funciona em <strong>computador, tablet e celular</strong>. Acompanhe seus prazos onde estiver.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="group bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-800 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">100% Seguro</h3>
              <p className="text-gray-600">
                <strong>Criptografia total</strong>, servidores Google Cloud e conformidade com LGPD. Sigilo garantido.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="como-funciona" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Como Funciona?
            </h2>
            <p className="text-xl text-gray-600">
              Em apenas <strong>4 passos simples</strong>, você terá controle total dos seus prazos
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center">
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-1/2 w-full h-1 bg-gradient-to-r from-blue-500 to-transparent z-0"></div>
                )}
                
                <div className="relative z-10">
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-xl">
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold mb-4">
                    Passo {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              O Que Advogados Dizem
            </h2>
            <p className="text-xl text-blue-200">
              Mais de <strong className="text-yellow-400">500 escritórios</strong> já confiam no JurisPrazo
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-lg text-white/90 mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-4">
                  <div className="text-4xl">{t.avatar}</div>
                  <div>
                    <div className="font-bold text-white">{t.name}</div>
                    <div className="text-blue-300 text-sm">{t.role}</div>
                    <div className="text-blue-400 text-xs">{t.city}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-black text-blue-600 mb-2">500+</div>
              <div className="text-gray-600 font-medium">Escritórios Ativos</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black text-blue-600 mb-2">50K+</div>
              <div className="text-gray-600 font-medium">Prazos Monitorados</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black text-blue-600 mb-2">99.9%</div>
              <div className="text-gray-600 font-medium">Uptime</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black text-green-500 mb-2">0</div>
              <div className="text-gray-600 font-medium">Prazos Perdidos</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Perguntas Frequentes
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-gray-600">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-8">
            <Award className="w-5 h-5 text-yellow-400" />
            <span className="font-medium">Garantia de 30 dias</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-black mb-6">
            Comece Agora e<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
              Proteja Sua Carreira
            </span>
          </h2>

          <p className="text-xl md:text-2xl text-white/80 mb-10 max-w-2xl mx-auto">
            Um prazo perdido pode custar sua reputação.
            O JurisPrazo é o investimento mais inteligente que você pode fazer.
          </p>

          <button
            onClick={() => onNavigate && onNavigate('auth:cadastro')}
            className="group inline-flex items-center gap-3 px-10 py-5 bg-white hover:bg-gray-100 rounded-2xl font-bold text-xl text-blue-700 transition-all duration-300 shadow-2xl hover:shadow-white/25 transform hover:-translate-y-1"
          >
            Criar Minha Conta Grátis
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>

          <p className="mt-6 text-white/60 text-sm">
            Sem cartão de crédito • Cancele quando quiser • Suporte 24/7
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">JurisPrazo</span>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-slate-400 text-sm">
                © 2026 JurisPrazo. Todos os direitos reservados.
              </p>
              <p className="text-slate-500 text-xs mt-1">
                Desenvolvido para advogados brasileiros
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
